/**
 * Write-back state for a Google-Sheet job (gsheet-io-01 / S5.4).
 *
 * Renders nothing for a file-upload job: the endpoint answers "none" and this component
 * disappears, so the job page is unchanged for every existing job.
 *
 * It is the only place a user learns that Google refused the write — the dial run itself
 * deliberately succeeds regardless (the DB is the source of truth), so without this the
 * failure would be invisible. Hence the retry button lives here too.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { gsheetsApi } from '../services/api';
import { WritebackStatus } from '../types';

const LABEL: Record<string, string> = {
  pending: 'Waiting to write to the sheet',
  partial: 'Writing results to the sheet…',
  done: 'Written to the Google Sheet',
  dry_run: 'Preview only — nothing written to the sheet',
  failed: 'Could not write to the sheet',
};

const TONE: Record<string, { bg: string; fg: string }> = {
  pending: { bg: 'var(--status-neutral-bg)', fg: 'var(--text-soft)' },
  partial: { bg: 'var(--status-analysis-processing-bg)', fg: 'var(--text-strong)' },
  done: { bg: 'var(--status-success-bg, var(--status-neutral-bg))', fg: 'var(--text-strong)' },
  dry_run: { bg: 'var(--status-neutral-bg)', fg: 'var(--text-soft)' },
  failed: { bg: 'var(--status-danger-bg)', fg: 'var(--status-danger-fg)' },
};

interface Props {
  jobId: string;
}

const WritebackBadge: React.FC<Props> = ({ jobId }) => {
  const [info, setInfo] = useState<WritebackStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setInfo(await gsheetsApi.writebackStatus(jobId));
    } catch {
      setInfo(null);              // feature off, or not our job — stay silent
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  // Poll only while a write is actually in flight.
  useEffect(() => {
    if (info?.status !== 'partial' && info?.status !== 'pending') return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [info?.status, load]);

  const retry = async () => {
    setBusy(true);
    try {
      await gsheetsApi.retryWriteback(jobId);
    } catch {
      /* the reloaded status carries the reason */
    } finally {
      setBusy(false);
      load();
    }
  };

  if (!info || info.status === 'none') return null;

  const tone = TONE[info.status] ?? TONE.pending;
  const columns = Object.values(info.blocks || {}).flatMap((b) => b.headers);

  return (
    <div style={{ background: tone.bg, color: tone.fg, borderRadius: 7, padding: '8px 12px',
                  fontSize: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600 }}>{LABEL[info.status] ?? info.status}</span>
      {info.status === 'done' && info.cells > 0 && <span>{info.cells.toLocaleString()} cells</span>}
      {columns.length > 0 && (
        <span style={{ opacity: 0.8 }}>
          {columns.length === 1 ? 'column' : 'columns'}: {columns.join(', ')}
        </span>
      )}
      {info.error && <span style={{ opacity: 0.9 }}>{info.error}</span>}
      {info.status === 'failed' && (
        <button
          onClick={retry}
          disabled={busy}
          style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 5, fontSize: 12,
                   fontWeight: 600, cursor: busy ? 'progress' : 'pointer',
                   border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
        >
          {busy ? 'Retrying…' : 'Retry write-back'}
        </button>
      )}
    </div>
  );
};

export default WritebackBadge;
